"use strict";
// Feature: address-geolocation, Task 10.1
//
// Legacy_Address_String backfill planner — pure. Given a batch of records
// that may carry only a legacy free-text address string, emits a plan of
// idempotent actions:
//
//   - `skip`                — record already has structured address AND
//                             location; nothing to do.
//   - `skip-no-legacy`      — record has neither legacy string nor
//                             structured address; nothing to plan.
//   - `attempt-geocode`     — record has a legacy string but no location;
//                             caller should call
//                             `GeocodingProvider.geocode(legacyString)`.
//   - `awaiting-confirmation` — the record has a machine-parsed structured
//                             address that a treasurer has not confirmed;
//                             legacy string is retained until confirmed
//                             (Req 11.4).
//
// The planner is I/O-free. It does not geocode, it does not mutate. It
// tells the caller what to do next. Owner scoping is enforced by requiring
// the caller to pass the resolved `ownerId` — records whose `ownerId`
// mismatches are silently skipped (defence in depth, Req 11.4).
Object.defineProperty(exports, "__esModule", { value: true });
exports.planLegacyBackfill = void 0;
/**
 * Build a deterministic backfill plan. The same input always produces the
 * same output. Repeated runs skip already-confirmed records (idempotency
 * per Req 11.4).
 */
function planLegacyBackfill(candidates, resolvedOwnerId) {
    const steps = [];
    const counts = {
        skip: 0,
        "skip-no-legacy": 0,
        "attempt-geocode": 0,
        "awaiting-confirmation": 0,
        crossOwnerSkipped: 0,
    };
    for (const c of candidates) {
        if (c.ownerId !== resolvedOwnerId) {
            counts.crossOwnerSkipped++;
            steps.push({
                id: c.id,
                action: "skip",
                skippedReason: "cross-owner",
            });
            continue;
        }
        const hasStructured = !!c.structuredAddress;
        const hasLocation = !!c.location;
        const hasLegacy = typeof c.legacyAddress === "string" && c.legacyAddress.trim().length > 0;
        const confirmed = c.structuredAddressConfirmed === true;
        if (hasStructured && hasLocation && confirmed) {
            counts.skip++;
            steps.push({ id: c.id, action: "skip" });
            continue;
        }
        if (hasStructured && !confirmed) {
            counts["awaiting-confirmation"]++;
            steps.push({ id: c.id, action: "awaiting-confirmation" });
            continue;
        }
        if (hasLegacy) {
            counts["attempt-geocode"]++;
            steps.push({
                id: c.id,
                action: "attempt-geocode",
                geocodeInput: c.legacyAddress.trim(),
            });
            continue;
        }
        counts["skip-no-legacy"]++;
        steps.push({ id: c.id, action: "skip-no-legacy" });
    }
    return { ownerId: resolvedOwnerId, steps, counts };
}
exports.planLegacyBackfill = planLegacyBackfill;
